import type { Database } from "../types/types.gen"

export const FACEBOOK_CLICK_ID_KEY = "fbc"
const GENDER_KEY = "gender"
const HEIGHT_KEY = "height"
const WEIGHT_KEY = "weight"
const GOAL_WEIGHT_KEY = "goalWeight"
const AGE_KEY = "age"
const USE_METRIC_OR_IMPERIAL_KEY = "useMetricOrImperial"
const ACTIVITY_LEVEL_KEY = "activityLevel"
const ACHIEVED_GOAL_KEY = "achievedGoal"
const MAIN_GOAL_KEY = "mainGoal"
const NUTRITION_GOAL_KEY = "nutritionGoal"
const FUNNEL_SOURCE_KEY = "funnelSource"

// -- Supabase Enums --
type ActivityLevelEnum = Database["public"]["Enums"]["activity_level_enum"]
type AchieveResultsEnum = Database["public"]["Enums"]["achieve_results_enum"]
type PrimaryNutritionGoalEnum =
  Database["public"]["Enums"]["primary_nutrition_goal_enum"]

/* -------------------------------------------
   1) Facebook Tracking
------------------------------------------- */
export function addFacebookParamsToLocalStorage(locationSearch: string) {
  const urlSearchParams = new URLSearchParams(locationSearch)
  addFbcToLocalStorage(urlSearchParams)
}

function addFbcToLocalStorage(urlSearchParams: URLSearchParams) {
  const fbclid = urlSearchParams.get("fbclid")
  if (fbclid) {
    const creationTime = Date.now()
    const subdomainIndex = import.meta.env.MODE === "development" ? 0 : 1

    const fbc = `fb.${subdomainIndex}.${creationTime}.${fbclid}`
    localStorage.setItem(FACEBOOK_CLICK_ID_KEY, fbc)
  }
}

/* -------------------------------------------
   2) Gender (non-enum)
------------------------------------------- */
export function addGenderToLocalStorage(isFemale: boolean) {
  const gender = isFemale ? "female" : "male"
  localStorage.setItem(GENDER_KEY, gender)
}

export function getIsFemaleFromLocalStorage() {
  return localStorage.getItem(GENDER_KEY) === "female"
}

/* -------------------------------------------
   3) Height / Weight / Age (numbers)
------------------------------------------- */
export function addHeightToLocalStorage(height: number) {
  localStorage.setItem(HEIGHT_KEY, height.toString())
}
export function getHeightFromLocalStorage() {
  return localStorage.getItem(HEIGHT_KEY)
}

export function addWeightToLocalStorage(weight: number) {
  localStorage.setItem(WEIGHT_KEY, weight.toString())
}
export function getWeightFromLocalStorage() {
  return localStorage.getItem(WEIGHT_KEY)
}

export function addGoalWeightToLocalStorage(goalWeight: number) {
  localStorage.setItem(GOAL_WEIGHT_KEY, goalWeight.toString())
}
export function getGoalWeightFromLocalStorage() {
  return localStorage.getItem(GOAL_WEIGHT_KEY)
}

export function addAgeToLocalStorage(age: number) {
  localStorage.setItem(AGE_KEY, age.toString())
}
export function getAgeFromLocalStorage() {
  return localStorage.getItem(AGE_KEY)
}

/* -------------------------------------------
   4) Unit Preference (boolean vs. enum)
   - If your DB column is boolean, keep boolean
   - If your DB uses "metric"/"imperial", store an enum
------------------------------------------- */
export function addUseMetricToLocalStorage(useMetric: boolean) {
  localStorage.setItem(USE_METRIC_OR_IMPERIAL_KEY, useMetric.toString())
}
export function getUseMetricFromLocalStorage() {
  return localStorage.getItem(USE_METRIC_OR_IMPERIAL_KEY) === "true"
}

/* -------------------------------------------
   5) Activity Level (Supabase enum)
------------------------------------------- */
export function addActivityLevelToLocalStorage(
  activityLevel: ActivityLevelEnum,
) {
  localStorage.setItem(ACTIVITY_LEVEL_KEY, activityLevel)
}
export function getActivityLevelFromLocalStorage(): ActivityLevelEnum | null {
  const value = localStorage.getItem(ACTIVITY_LEVEL_KEY)
  return value ? (value as ActivityLevelEnum) : null
}

/* -------------------------------------------
   6) Achieved Goal (Supabase enum)
------------------------------------------- */
export function addAchievedGoalToLocalStorage(
  achievedGoal: AchieveResultsEnum,
) {
  localStorage.setItem(ACHIEVED_GOAL_KEY, achievedGoal)
}
export function getAchievedGoalFromLocalStorage(): AchieveResultsEnum | null {
  const value = localStorage.getItem(ACHIEVED_GOAL_KEY)
  return value ? (value as AchieveResultsEnum) : null
}

/* -------------------------------------------
   7) Main Goal (onboarding answer)
------------------------------------------- */
export function addMainGoalToLocalStorage(mainGoal: string) {
  localStorage.setItem(MAIN_GOAL_KEY, mainGoal)
}
export function getMainGoalFromLocalStorage(): string | null {
  return localStorage.getItem(MAIN_GOAL_KEY)
}

/* -------------------------------------------
   8) Nutrition Goal (Supabase enum)
------------------------------------------- */
export function addNutritionGoalToLocalStorage(
  nutritionGoal: PrimaryNutritionGoalEnum,
) {
  localStorage.setItem(NUTRITION_GOAL_KEY, nutritionGoal)
}
export function getNutritionGoalFromLocalStorage(): PrimaryNutritionGoalEnum | null {
  const value = localStorage.getItem(NUTRITION_GOAL_KEY)
  return value ? (value as PrimaryNutritionGoalEnum) : null
}

/* -------------------------------------------
   9) Funnel Source
------------------------------------------- */
export function addFunnelSourceToLocalStorage(source: string) {
  localStorage.setItem(FUNNEL_SOURCE_KEY, source)
}
export function getFunnelSourceFromLocalStorage(): string | null {
  return localStorage.getItem(FUNNEL_SOURCE_KEY)
}
